/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description Helper function to handle follow up modals after sending a message
 */

import {
  type ExtraThunkArg,
  type InitialMessageData,
  type Listable,
  MODAL_TYPE,
  type MessageData,
  type SendMessageArgs,
} from '@zcode/state-type'
import { ALERT_CREATION_TOAST_TYPE } from '../../components/Notification/constants'
import { REFERRERS } from '../../constants/sign_in'
import { pushSignInUrl, pushSignUpUrl } from '../../modules/account/thunks'
import { setToastType } from '../../modules/currentSearchView/actions'
import { receiveResidentQuotesModalData } from '../../modules/modals/actions/residentQuotesModal'
import { selectResidentQuotesModalOpen } from '../../modules/modals/selectors/residentQuotes'
import { openModal } from '../../modules/modals/thunks/index'
import { openMultiMessageModal } from '../../modules/modals/thunks/multiMessageModal/openMultiMessageModal'
import type { Dispatch, GetState } from '../../modules/types'
import { setPendingMessage } from './pendingMessage'

type HandleFollowupModalArgs = {
  config: {
    analyticChannel: SendMessageArgs['analyticChannel']
  } & Pick<SendMessageArgs, 'dlcMessagedProperties' | 'entity'>
  equalCTAIntent?: SendMessageArgs['equalCTAIntent']
  formType?: SendMessageArgs['formType']
  isDlcTestVariation?: boolean
  messageData: InitialMessageData | MessageData
  selectedFloorplanId?: SendMessageArgs['selectedFloorplanId']
  shouldLimitLeads: boolean | undefined
  showPostLeadAfterMM: boolean | undefined
  similarListings: Listable[] | null
  skipResidentQuotesModal?: boolean
} & (
  | {
      showPreLeadSignInModal: true
      sendMessageArgs: SendMessageArgs
    }
  | {
      showPreLeadSignInModal?: false
      sendMessageArgs?: SendMessageArgs
    }
)

export const handleFollowUpModal =
  ({
    config,
    equalCTAIntent,
    formType,
    isDlcTestVariation,
    messageData,
    selectedFloorplanId,
    sendMessageArgs,
    similarListings,
    shouldLimitLeads,
    showPostLeadAfterMM,
    showPreLeadSignInModal,
    skipResidentQuotesModal,
  }: HandleFollowupModalArgs) =>
  (dispatch: Dispatch, getState: GetState, { subscribe }: ExtraThunkArg) => {
    const { analyticChannel, dlcMessagedProperties, entity } = config
    const { name, origin, phone } = messageData
    const email = messageData.email ?? undefined
    const showMMModal =
      !!similarListings?.length && !shouldLimitLeads && (entity?.isRentpath || !entity?.isMmPrevent)
    const showResidentQuotesModal = !skipResidentQuotesModal && entity?.hasResidentQuotes

    //only pass these props along to MM modal if on test variation of DLC
    const dlcMessageData = {
      equalCTAIntent,
      formType,
      messageData: {
        email,
        name,
        origin,
        phone,
      },
      selectedFloorplanId,
    }

    if (showPreLeadSignInModal) {
      setPendingMessage(sendMessageArgs)
      dispatch(pushSignInUrl({ email, referrer: REFERRERS.MESSAGE_EXISTING_USER }))
    } else if (showResidentQuotesModal) {
      // next action needs to call handleFollowUpModal again so that MM modal is opened
      // after resident quotes modal is closed
      const nextAction = () => {
        if (showMMModal) {
          const unsubscribe = subscribe(() => {
            // we have to wait for the resident quotes modal to close
            // before opening the MM modal because the MM modal
            // ends up closing before it even shows due to the location change logic in AppContainer
            if (!selectResidentQuotesModalOpen(getState())) {
              dispatch(
                handleFollowUpModal({
                  config,
                  equalCTAIntent,
                  formType,
                  isDlcTestVariation,
                  messageData,
                  selectedFloorplanId,
                  sendMessageArgs,
                  similarListings,
                  shouldLimitLeads,
                  showPostLeadAfterMM,
                  showPreLeadSignInModal,
                  skipResidentQuotesModal: true,
                })
              )
              unsubscribe()
            }
          })
        }
      }

      dispatch(
        receiveResidentQuotesModalData({
          isPostLead: true,
          nextAction,
          entity: config.entity,
        })
      )
      dispatch(openModal(MODAL_TYPE.RESIDENT_QUOTES))
    } else if (showMMModal) {
      dispatch(setToastType(ALERT_CREATION_TOAST_TYPE.MESSAGE_SENT))
      // dispatch to show Multi Message modal if there are similar listings
      dispatch(
        openMultiMessageModal({
          analyticChannel,
          dlcMessagedProperties,
          entity,
          origin,
          similarListings,
          ...(showPostLeadAfterMM && {
            postLeadData: {
              name,
              email,
            },
          }),
          ...(isDlcTestVariation && dlcMessageData),
        })
      )
    } else if (showPostLeadAfterMM && !showMMModal) {
      // PostLead modal gets triggered onclose of MM Modal...
      // if we're not showing the MM Modal, we're just going to trigger this modal manually
      dispatch(
        pushSignUpUrl({
          email,
          first_name: name?.includes(' ') ? name.substring(0, name.indexOf(' ')) : name,
          last_name: name?.includes(' ') ? name.substring(name.indexOf(' ') + 1) : undefined,
          referrer: REFERRERS.POST_LEAD_SIGN_UP,
        })
      )
    }
  }
